import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ExpandMore,
  ContentCopy,
  Link as LinkIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const ManagerProfile = () => {
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
  });
  const [organizationFormData, setOrganizationFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    tax_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [message, setMessage] = useState('');
  const [organizationMessage, setOrganizationMessage] = useState('');
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoiceLinkMessage, setInvoiceLinkMessage] = useState('');

  useEffect(() => {
    fetchProfile();
    fetchOrganization();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInvoicesLoading(true);
      try {
        const response = await api.get('/invoices/');
        let list = [];
        if (Array.isArray(response.data)) {
          list = response.data;
        } else if (response.data?.results && Array.isArray(response.data.results)) {
          list = response.data.results;
        } else if (response.data && typeof response.data === 'object') {
          const arr = Object.values(response.data).find((v) => Array.isArray(v));
          list = arr || [];
        }
        if (!cancelled) {
          list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          setInvoices(list);
        }
      } catch {
        if (!cancelled) setInvoices([]);
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/managers/profile/');
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone_number: response.data.phone_number || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      // If profile doesn't exist, show form to create it
      if (error.response?.status === 404) {
        setMessage('Please complete your profile information below.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/organizations/organizations/profile/');
      setOrganization(response.data);
      setOrganizationFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        tax_id: response.data.tax_id || '',
      });
    } catch (error) {
      console.error('Error fetching organization:', error);
      // Organization might not exist yet, that's okay
      if (error.response?.status === 404) {
        setOrganizationMessage('Please complete your organization information below.');
      } else if (error.response?.status !== 404) {
        console.error('Unexpected error fetching organization:', error);
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOrganizationChange = (e) => {
    setOrganizationFormData({
      ...organizationFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Use PUT if profile exists, POST if it doesn't
      const method = profile ? 'put' : 'post';
      await api[method]('/managers/profile/', formData);
      setMessage('Profile saved successfully');
      fetchProfile();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error saving profile. Please check your input.';
      setMessage(errorMsg);
      console.error('Error saving profile:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  const handleOrganizationSubmit = async (e) => {
    e.preventDefault();
    setSavingOrganization(true);
    setOrganizationMessage('');

    try {
      // Use PUT if organization exists, POST if it doesn't
      const method = organization ? 'put' : 'post';
      await api[method]('/organizations/organizations/profile/', organizationFormData);
      setOrganizationMessage('Organization saved successfully');
      fetchOrganization();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error saving organization. Please check your input.';
      setOrganizationMessage(errorMsg);
      console.error('Error saving organization:', error.response?.data || error);
    } finally {
      setSavingOrganization(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    setPasswordMessage('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage('');

    // Validation
    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      setPasswordMessage('All fields are required');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordMessage('New password must be at least 8 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage('New passwords do not match');
      return;
    }

    setChangingPassword(true);

    try {
      await api.post('/auth/password/change/', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      setPasswordMessage('Password changed successfully');
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Failed to change password. Please check your current password.';
      setPasswordMessage(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const copyPublicPayUrl = async (url) => {
    if (!url) return;
    setInvoiceLinkMessage('');
    try {
      await navigator.clipboard.writeText(url);
      setInvoiceLinkMessage('Payment link copied to clipboard.');
    } catch {
      setInvoiceLinkMessage('Could not copy. Select the link and copy manually.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Typography>Loading...</Typography>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Manager Profile
        </Typography>
        
        {profile && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current Profile Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Name
                </Typography>
                <Typography variant="body1">{profile.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{profile.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Phone Number
                </Typography>
                <Typography variant="body1">{profile.phone_number || 'Not provided'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {organization && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Organization Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Organization Name
                </Typography>
                <Typography variant="body1">{organization.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{organization.email || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body1">{organization.phone || 'Not provided'}</Typography>
              </Grid>
              {organization.tax_id && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Tax ID / EIN
                  </Typography>
                  <Typography variant="body1">{organization.tax_id}</Typography>
                </Grid>
              )}
              {(organization.address || organization.city || organization.state || organization.zip_code) && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {[
                      organization.address,
                      organization.city,
                      organization.state,
                      organization.zip_code
                    ].filter(Boolean).join(', ') || 'Not provided'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Public invoice payment links
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Share these links with customers to pay online without logging in. Same signed URLs as invoice emails;
            they expire after the configured period (see server settings).
          </Typography>
          {invoiceLinkMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInvoiceLinkMessage('')}>
              {invoiceLinkMessage}
            </Alert>
          )}
          {invoicesLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading invoices…
            </Typography>
          ) : invoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No invoices yet.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ minWidth: 280 }}>Public pay link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} hover>
                      <TableCell>{inv.invoice_number || inv.id}</TableCell>
                      <TableCell>{inv.customer_name || '—'}</TableCell>
                      <TableCell>{inv.status || '—'}</TableCell>
                      <TableCell>
                        {inv.public_pay_url ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 400 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={inv.public_pay_url}
                              InputProps={{
                                readOnly: true,
                                sx: { fontSize: '0.75rem', fontFamily: 'monospace' },
                              }}
                              onFocus={(e) => e.target.select()}
                            />
                            <Tooltip title="Open in new tab">
                              <IconButton
                                size="small"
                                component="a"
                                href={inv.public_pay_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open pay link"
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy link">
                              <IconButton
                                size="small"
                                onClick={() => copyPublicPayUrl(inv.public_pay_url)}
                                aria-label="Copy pay link"
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {profile ? 'Update Profile' : 'Create Profile'}
          </Typography>
          
          {message && (
            <Alert 
              severity={message.includes('successfully') ? 'success' : 'error'} 
              sx={{ mb: 2 }}
              onClose={() => setMessage('')}
            >
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{ mt: 2 }}
                >
                  {saving ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {organization ? 'Update Organization Details' : 'Create Organization Details'}
          </Typography>
          
          {organizationMessage && (
            <Alert 
              severity={organizationMessage.includes('successfully') ? 'success' : organizationMessage.includes('Please') ? 'info' : 'error'} 
              sx={{ mb: 2 }}
              onClose={() => setOrganizationMessage('')}
            >
              {organizationMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleOrganizationSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  name="name"
                  value={organizationFormData.name}
                  onChange={handleOrganizationChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={organizationFormData.address}
                  onChange={handleOrganizationChange}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={organizationFormData.city}
                  onChange={handleOrganizationChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="state"
                    value={organizationFormData.state}
                    onChange={handleOrganizationChange}
                    label="State"
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    <MenuItem value="AL">AL - Alabama</MenuItem>
                    <MenuItem value="AK">AK - Alaska</MenuItem>
                    <MenuItem value="AZ">AZ - Arizona</MenuItem>
                    <MenuItem value="AR">AR - Arkansas</MenuItem>
                    <MenuItem value="CA">CA - California</MenuItem>
                    <MenuItem value="CO">CO - Colorado</MenuItem>
                    <MenuItem value="CT">CT - Connecticut</MenuItem>
                    <MenuItem value="DE">DE - Delaware</MenuItem>
                    <MenuItem value="FL">FL - Florida</MenuItem>
                    <MenuItem value="GA">GA - Georgia</MenuItem>
                    <MenuItem value="HI">HI - Hawaii</MenuItem>
                    <MenuItem value="ID">ID - Idaho</MenuItem>
                    <MenuItem value="IL">IL - Illinois</MenuItem>
                    <MenuItem value="IN">IN - Indiana</MenuItem>
                    <MenuItem value="IA">IA - Iowa</MenuItem>
                    <MenuItem value="KS">KS - Kansas</MenuItem>
                    <MenuItem value="KY">KY - Kentucky</MenuItem>
                    <MenuItem value="LA">LA - Louisiana</MenuItem>
                    <MenuItem value="ME">ME - Maine</MenuItem>
                    <MenuItem value="MD">MD - Maryland</MenuItem>
                    <MenuItem value="MA">MA - Massachusetts</MenuItem>
                    <MenuItem value="MI">MI - Michigan</MenuItem>
                    <MenuItem value="MN">MN - Minnesota</MenuItem>
                    <MenuItem value="MS">MS - Mississippi</MenuItem>
                    <MenuItem value="MO">MO - Missouri</MenuItem>
                    <MenuItem value="MT">MT - Montana</MenuItem>
                    <MenuItem value="NE">NE - Nebraska</MenuItem>
                    <MenuItem value="NV">NV - Nevada</MenuItem>
                    <MenuItem value="NH">NH - New Hampshire</MenuItem>
                    <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                    <MenuItem value="NM">NM - New Mexico</MenuItem>
                    <MenuItem value="NY">NY - New York</MenuItem>
                    <MenuItem value="NC">NC - North Carolina</MenuItem>
                    <MenuItem value="ND">ND - North Dakota</MenuItem>
                    <MenuItem value="OH">OH - Ohio</MenuItem>
                    <MenuItem value="OK">OK - Oklahoma</MenuItem>
                    <MenuItem value="OR">OR - Oregon</MenuItem>
                    <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                    <MenuItem value="RI">RI - Rhode Island</MenuItem>
                    <MenuItem value="SC">SC - South Carolina</MenuItem>
                    <MenuItem value="SD">SD - South Dakota</MenuItem>
                    <MenuItem value="TN">TN - Tennessee</MenuItem>
                    <MenuItem value="TX">TX - Texas</MenuItem>
                    <MenuItem value="UT">UT - Utah</MenuItem>
                    <MenuItem value="VT">VT - Vermont</MenuItem>
                    <MenuItem value="VA">VA - Virginia</MenuItem>
                    <MenuItem value="WA">WA - Washington</MenuItem>
                    <MenuItem value="WV">WV - West Virginia</MenuItem>
                    <MenuItem value="WI">WI - Wisconsin</MenuItem>
                    <MenuItem value="WY">WY - Wyoming</MenuItem>
                    <MenuItem value="DC">DC - District of Columbia</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  name="zip_code"
                  value={organizationFormData.zip_code}
                  onChange={handleOrganizationChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={organizationFormData.phone}
                  onChange={handleOrganizationChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={organizationFormData.email}
                  onChange={handleOrganizationChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tax ID / EIN"
                  name="tax_id"
                  value={organizationFormData.tax_id}
                  onChange={handleOrganizationChange}
                  helperText="Tax ID or Employer Identification Number"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={savingOrganization}
                  sx={{ mt: 2 }}
                >
                  {savingOrganization ? 'Saving...' : organization ? 'Update Organization' : 'Create Organization'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Password Change Section */}
        <Accordion sx={{ mt: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="password-change-content"
            id="password-change-header"
          >
            <Typography variant="h6">Change Password</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {passwordMessage && (
              <Alert 
                severity={passwordMessage.includes('success') ? 'success' : 'error'} 
                sx={{ mb: 2 }}
              >
                {passwordMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="old_password"
                  type={showPasswords.old ? 'text' : 'password'}
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('old')}
                          edge="end"
                        >
                          {showPasswords.old ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="new_password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  helperText="Must be at least 8 characters long"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('new')}
                          edge="end"
                        >
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirm_password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('confirm')}
                          edge="end"
                        >
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={changingPassword}
                  size="large"
                >
                  {changingPassword ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Grid>
            </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Container>
    </Layout>
  );
};

export default ManagerProfile;

